import React, { useCallback } from 'react';
import cn from 'classnames';

import IMG_SLICK from 'assets/images/logos/slick-logo-text.png';
import validatePassword from 'bl/validation/validatePassword.bl';
import validateVerificationCode from 'bl/validation/validateVerificationCode.bl';
import Button from 'components/form/Button.component';
import CodeInput from 'components/form/CodeInput.component';
import FormField from 'components/form/FormField.component';
import PasswordInput from 'components/form/PasswordInput.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Space from 'components/generic/Space.component';
import S from 'constants/string.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import call from 'utils/functions/call.util';

import CN from './ChangePasswordPanel.module.scss';

const CTAG = 'ChangePasswordPanel';

const CODE_ERROR_MESSAGE = 'Please enter a valid code';
const PASSWORD_ERROR_MESSAGE =
  'Password needs to be at least 8 characters in length and contain an uppercase character and a number.';

const PLACEHOLDER = '8 characters, 1 uppercase, 1 number';

const ChangePasswordPanel = ({ tag: tagFromProps, onNext }) => {
  const tag = fromAnyToTrimmed(tagFromProps);

  const [code, setCode] = useStateWhileMounted(S.empty);
  const [pass, setPass] = useStateWhileMounted(S.empty);

  const isCodeValid = validateVerificationCode({ value: code });
  const isPassValid = validatePassword({ value: pass });
  const disabled = !isCodeValid || !isPassValid;

  const handleCode = useCallback(
    ({ string, is }) => {
      setCode(string);

      const isFullyEntered = is.valid;
      if (!isFullyEntered) return { error: null };

      return { error: validateVerificationCode({ value: string }) ? null : CODE_ERROR_MESSAGE };
    },
    [setCode],
  );

  const handlePass = useCallback(
    ({ dirty, string }) => {
      setPass(string);

      if (!dirty) return { error: null };
      return { error: validatePassword({ value: string }) ? null : PASSWORD_ERROR_MESSAGE };
    },
    [setPass],
  );

  const handleNext = useCallback(
    //
    () => {
      if (disabled) return;
      call(`${CTAG}.onNext`, onNext, { tag, code, pass });
    },
    [code, disabled, onNext, pass, tag],
  );

  const inputClasses = cn({
    [CN.inputContainer]: true,
    [CN.hasError]: disabled && !isPassValid && pass.trim() !== '',
  });

  return (
    <div data-bem="ChangePasswordPanel" className={CN.component}>
      <img src={IMG_SLICK} alt="Slick logo" className={CN.logo} />
      <div className={CN.title}>
        Your <span className={CN.bold}>code</span> is on it{S.apos}s way {S.userLogin}
      </div>
      <div className={CN.subTitle}>
        Enter the code you received via email. Once you enter your code you can reset your password and login.
      </div>
      <FormField label="Code" input={CodeInput} onChange={handleCode} onKeyDown={handleCode} autofocus />
      {isCodeValid && (
        <div className={inputClasses}>
          <FormField
            input={PasswordInput}
            name="password"
            label="New password"
            placeholder={PLACEHOLDER}
            onChange={handlePass}
            renderError="always"
          />
        </div>
      )}
      <div className={CN.alertOuter}>
        <AlertPanel tag="ChangePasswordPanel__alert" variant="alert">
          <div>
            <Space bold>Be aware:</Space>
            We will never ask you for your password or tell you what to change your new password to. If in doubt, get in
            touch via our live chat bubble
          </div>
        </AlertPanel>
      </div>
      <div className={CN.buttonContainer}>
        <Button variant="primaryForce" width="max" height="max" onClick={handleNext} disabled={disabled}>
          <span className={CN.buttonFont}>RESET PASSWORD</span>
        </Button>
      </div>
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default ChangePasswordPanel;
